import GestionArchivo from "../Archivo/GestionArchivo";
import IntervaloLectura from "./IntervaloLectura";
import { intervaloEnMinutos, mostrarIntervalo } from "./intervaloLecturaUtils";
// src/components/Sensores/AdministrarSensores.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import sensorService, { SignalType } from "../../services/sensorService";
import { recomendarPinesSensor, recomendarTipoSenal } from "./pinRecommendations";
import { obtenerMisDispositivos } from "../../services/dispositivoService";
import usuarioService from "../../services/usuarioService";
import { usePermisos } from "../../context/PermisosContext";
import { isSuperAdmin } from "../../services/authUtils";


/* ===== normalizadores ===== */
const normalizarPermisos = (p) => {
  if (Array.isArray(p)) return p.map(String);
  if (typeof p === "string" && p.trim().length) {
    return p.split(",").map((x) => x.trim()).filter(Boolean).map(String);
  }
  return [];
};

// Convierte varias formas comunes de payload a array
const toArray = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.usuarios)) return data.usuarios;
  return [];
};

// Aplana payload de /api/sensores/todos (estructura anidada)
const aplanarSensores = (sensoresData) =>
  (sensoresData || []).flatMap((e) =>
    (e.dispositivos || []).flatMap((d) =>
      (d.sensores || []).map((s) => ({
        id: s.id,
        estanqueNombre: e.estanqueNombre || "Sin estanque",
        nombre: s.nombre ?? "",
        descripcion: s.descripcion ?? "",
        tipoParametroId: s.tipoParametroId,
        dispositivoId: d.dispositivoId,
        signalType: typeof s.signalType === "number" ? s.signalType : SignalType.Uart,
        pinPrincipal: s.pinPrincipal ?? "",
        pinRx: s.pinRx ?? "",
        pinTx: s.pinTx ?? "",
        busId: s.busId ?? null,
        unidad: s.unidad ?? "",
        estaActivo: !!s.estaActivo,
        permisos: normalizarPermisos(s.permisos),
        ...mostrarIntervalo(s.tiempoSensor),
      }))
    )
  );

const AdministrarSensores = () => {
  const { can, canEdit } = usePermisos();

  // permisos efectivos
  const esSuper = useMemo(() => !!isSuperAdmin(), []);
  const puedeVer = esSuper || !!can?.("SENSORES_VIEW");
  const puedeEditar = esSuper || !!canEdit?.("SENSORES_EDIT");

  const [sensores, setSensores] = useState([]);
  const [dispositivos, setDispositivos] = useState([]);
  const [tiposSensor, setTiposSensor] = useState([]);
  const [usuarios, setUsuarios] = useState([]);

  const [mensaje, setMensaje] = useState("");
  const [error, setError] = useState("");
  const [flash, setFlash] = useState({}); // { [id]: { ok?: string, err?: string } }

  // cache de pines libres por combinación sensorId:dispositivoId:signalType
  const [pinesLibresMap, setPinesLibresMap] = useState({});
  const [forzarCambioPin, setForzarCambioPin] = useState({});
  const [busqueda, setBusqueda] = useState("");

  const sensoresPorEstanque = useMemo(() => {
    const grupos = new Map();
    const q = busqueda.trim().toLocaleLowerCase("es");
    sensores.filter((sensor) => {
      const tipo = tiposSensor.find((t) => String(t.id) === String(sensor.tipoParametroId))?.nombre;
      return !q || [sensor.nombre, sensor.descripcion, sensor.estanqueNombre, tipo, sensor.pinPrincipal]
        .some((v) => String(v ?? "").toLocaleLowerCase("es").includes(q));
    }).forEach((sensor) => {
      const estanque = sensor.estanqueNombre || "Sin estanque";
      if (!grupos.has(estanque)) grupos.set(estanque, []);
      grupos.get(estanque).push(sensor);
    });
    return Array.from(grupos.entries()).sort(([a], [b]) => a.localeCompare(b, "es"));
  }, [sensores, tiposSensor, busqueda]);

  const cargarDatos = useCallback(async () => {
    if (!puedeVer || !localStorage.getItem("token")) return;
    try {
      const [sensoresData, dispositivosData, tiposData, usuariosData] = await Promise.all([
        sensorService.obtenerTodosSensores(),
        obtenerMisDispositivos(),
        sensorService.obtenerTiposSensor(),
        esSuper
          ? usuarioService.listarPermisosTemporales()
          : usuarioService.obtenerUsuariosCreadosPorAdmin(),
      ]);

      const planos = aplanarSensores(sensoresData);
      setDispositivos(toArray(dispositivosData));
      setTiposSensor(toArray(tiposData));
      setUsuarios(toArray(usuariosData));
      setSensores(planos);
      setError("");
    } catch (err) {
      if (err?.isUnauthorized || err?.code === "ERR_CANCELED") return;
      console.error("Error cargando datos:", err);
      setError("Error al cargar datos.");
    }
  }, [puedeVer, esSuper]);

    // ✅ Re-fetch al montar, al volver foco/visibilidad y al cambiar la sesión
  useEffect(() => {
    let alive = true;

    const safeLoad = async () => {
      if (!alive) return;
      await cargarDatos();
    };

    safeLoad(); // al montar

    const onAuthChanged = () => {
      if (localStorage.getItem("token")) safeLoad();
    };

    window.addEventListener("auth-changed", onAuthChanged);

    return () => {
      alive = false;
      window.removeEventListener("auth-changed", onAuthChanged);
    };
  }, [cargarDatos]);

  // ✅ FALTABA ESTA FUNCIÓN
  const handleChange = useCallback((id, campo, valor) => {
    setSensores((prev) =>
      prev.map((s) => (s.id === id ? { ...s, [campo]: valor } : s))
    );
  }, []);

  const togglePermiso = useCallback((sensorId, usuarioId) => {
    if (!puedeEditar) return;
    setSensores((prev) =>
      prev.map((s) => {
        if (s.id !== sensorId) return s;
        const perms = normalizarPermisos(s.permisos);
        const uid = String(usuarioId);
        return perms.includes(uid)
          ? { ...s, permisos: perms.filter((p) => p !== uid) }
          : { ...s, permisos: [...perms, uid] };
      })
    );
  }, [puedeEditar]);

  // Mostrar flash por tarjeta
  const showFlash = useCallback((id, { ok, err }) => {
    setFlash((prev) => ({ ...prev, [id]: { ok, err } }));
    setTimeout(() => {
      setFlash((prev) => {
        const clone = { ...prev };
        delete clone[id];
        return clone;
      });
    }, 3000);
  }, []);

  // Cargar pines libres para un sensor (según su dispositivo y signalType)
  const cargarPinesLibres = useCallback(async (sensor) => {
    const dispositivoId = Number(sensor.dispositivoId);
    const signalType = Number(sensor.signalType);
    if (!dispositivoId || isNaN(dispositivoId)) return;
    if (signalType === null || signalType === undefined) return;

    const clave = `${sensor.id}:${sensor.dispositivoId}:${sensor.signalType}`;
    setPinesLibresMap((prev) => ({
      ...prev,
      [clave]: { loading: true, opciones: prev[clave]?.opciones || [] },
    }));
    try {
      const data = await sensorService.obtenerPinesLibres(dispositivoId, signalType, sensor.id);
      const opciones = data?.opciones || [];
      setPinesLibresMap((prev) => ({
        ...prev,
        [clave]: { loading: false, opciones },
      }));
    } catch (e) {
      console.error("Error obteniendo pines libres:", e);
      setPinesLibresMap((prev) => ({
        ...prev,
        [clave]: { loading: false, opciones: [] },
      }));
    }
  }, []);

  // Cuando cambie dispositivo o signalType de un sensor, recargar sus pines libres si no hay cache
  useEffect(() => {
    sensores.forEach((s) => {
      const key = `${s.id}:${s.dispositivoId}:${s.signalType}`;
      const cache = pinesLibresMap[key];
      if (!cache || (!cache.loading && (!cache.opciones || cache.opciones.length === 0))) {
        if (s.dispositivoId && (s.signalType !== null && s.signalType !== undefined)) {
          cargarPinesLibres(s);
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sensores]);

  const actualizarSensor = useCallback(
    async (sensor) => {
      if (!puedeEditar) return;

      try {
        const pinKey = `${sensor.id}:${sensor.dispositivoId}:${sensor.signalType}`;
        const ocupante = pinesLibresMap[pinKey]?.opciones?.find((o) => o.pin === sensor.pinPrincipal && o.ocupado);
        if (ocupante && !forzarCambioPin[sensor.id]) {
          showFlash(sensor.id, { err: `El GPIO ${sensor.pinPrincipal} está ocupado (${ocupante.motivo}). Elige otro pin.` });
          return;
        }
        if (forzarCambioPin[sensor.id] && !window.confirm(`Si el GPIO elegido está ocupado${ocupante ? ` por ${ocupante.motivo}` : ""}, el recurso que lo usa quedará archivado. ¿Forzar el cambio?`)) return;
        // Validaciones rápidas según tipo de señal
        const st = Number(sensor.signalType);
        if (![0,1,2].includes(st)) { showFlash(sensor.id, { err: "UART e I²C no están disponibles." }); return; }
        if (st === SignalType.Uart) {
          if (!sensor.pinRx || !sensor.pinTx) {
            showFlash(sensor.id, { err: "Para UART debes seleccionar Pin Rx y Pin Tx." });
            return;
          }
          if (sensor.pinRx === sensor.pinTx) {
            showFlash(sensor.id, { err: "Pin Rx y Pin Tx no pueden ser el mismo." });
            return;
          }
        } else {
          if (!sensor.pinPrincipal) {
            showFlash(sensor.id, { err: "Debes seleccionar un pin válido para este tipo de señal." });
            return;
          }
        }

        let tiempo;
        try {
          tiempo = sensor.tiempoSensor === "" ? null : intervaloEnMinutos(sensor.tiempoSensor, sensor.unidadTiempo);
        } catch (error) {
          showFlash(sensor.id, { err: error.message });
          return;
        }

        // 1) Actualizar sensor (V2)
        await sensorService.actualizarSensorV2(sensor.id, {
          nombre: sensor.nombre,
          descripcion: sensor.descripcion || "",
          tipoParametroId: Number(sensor.tipoParametroId),
          dispositivoId: Number(sensor.dispositivoId),
          signalType: Number(sensor.signalType),
          unidad: sensor.unidad || undefined,
          // Pines:
          pinPrincipal: st !== SignalType.Uart ? sensor.pinPrincipal || undefined : undefined,
          pinRx: st === SignalType.Uart ? sensor.pinRx || undefined : undefined,
          pinTx: st === SignalType.Uart ? sensor.pinTx || undefined : undefined,
          estaActivo: !!sensor.estaActivo,
          // Permisos como STRINGS (CSV policy en backend)
          permisos: normalizarPermisos(sensor.permisos),
          forzarCambioPin: !!forzarCambioPin[sensor.id],
        });

        // 2) Configuración de tiempo
        if (Number.isFinite(tiempo) && tiempo > 0) {
          try {
            await sensorService.actualizarTiempoSensor(sensor.id, {
              dispositivoId: Number(sensor.dispositivoId),
              tiempoSensor: tiempo,
            });
          } catch (errUpdate) {
            try {
              await sensorService.crearTiempoSensor({
                controladorId: sensor.id,
                dispositivoId: Number(sensor.dispositivoId),
                tiempoSensor: tiempo,
              });
            } catch (errCreate) {
              if (errCreate?.response?.status === 403) {
                showFlash(sensor.id, { ok: "Sensor actualizado. Frecuencia no guardada (permisos)." });
              } else if (errCreate?.response?.status !== 409) {
                console.error("Error guardando frecuencia:", errCreate);
                showFlash(sensor.id, { ok: "Sensor actualizado.", err: "No se pudo guardar la frecuencia." });
              }
            }
          }
        }

        showFlash(sensor.id, { ok: "Sensor actualizado correctamente." });
        setForzarCambioPin((prev) => ({ ...prev, [sensor.id]: false }));
        setMensaje("Sensor actualizado correctamente.");
        setPinesLibresMap({});
        setSensores(prev => [...prev]);
        setError("");
      } catch (err) {
        console.error("Error actualizando sensor:", err);
        const detalle = err?.response?.data;
        showFlash(sensor.id, { err: (typeof detalle === "string" ? detalle : detalle?.mensaje || detalle?.message) || "Error al actualizar el sensor." });
        setError("Error al actualizar el sensor.");
        setMensaje("");
      }
    },
    [puedeEditar, showFlash, forzarCambioPin, pinesLibresMap]
  );

  /* ====== UI helpers ====== */
  const isRecommended = (o, st) => {
    if (st === SignalType.Analog) {
      const hasADC1 = (o?.etiqueta || "").toUpperCase().includes("ADC1");
      return !!hasADC1 && !o?.riesgoso;
    }
    return !o?.riesgoso;
  };

  const splitOptions = (opciones, st) => {
    const rec = [];
    const adv = [];
    (opciones || []).forEach((o) => (isRecommended(o, st) ? rec.push(o) : adv.push(o)));
    return { rec, adv };
  };

  const renderOptgroups = (opciones, st, { disabledOption, allowOccupied = false } = {}) => {
    const { rec, adv } = splitOptions(opciones, st);
    const hasRec = rec.length > 0;
    const hasAdv = adv.length > 0;

    const renderOption = (o) => (
      <option key={o.pin} value={o.pin} disabled={(disabledOption && disabledOption(o)) || (o.ocupado && !allowOccupied)}>
        {o.etiqueta || o.pin} {o.ocupado ? ` (ocupado: ${o.motivo || "otro componente"})` : ""} {o.riesgoso ? " (riesgoso)" : ""}
      </option>
    );

    return (
      <>
        <option value="">Seleccione</option>
        {!hasAdv
          ? rec.map(renderOption)
          : (
            <>
              {hasRec && <optgroup label="Recomendados">{rec.map(renderOption)}</optgroup>}
              {hasAdv && <optgroup label="Avanzado">{adv.map(renderOption)}</optgroup>}
            </>
          )}
      </>
    );
  };

  const renderNotasTipo = (st) => {
    if (st === SignalType.Analog) {
      return (
        <p className="text-xs text-neutral-300 mt-1">
          Sugerencia: usa <b>ADC1 (GPIO32, 33, 34, 35, 36 o 39)</b> para analógicos. Evita ADC2 porque el Wi-Fi lo ocupa.
        </p>
      );
    }
    if (st === SignalType.OneWire) {
      return (
        <p className="text-xs text-neutral-300 mt-1">
          DS18B20: una sola sonda por GPIO; recuerda el <b>pull-up de 4.7 kΩ</b> entre DATA y 3V3.
        </p>
      );
    }
    return null;
  };

  const renderSelectPines = (sensor) => {
    const key = `${sensor.id}:${sensor.dispositivoId}:${sensor.signalType}`;
    const cache = pinesLibresMap[key];
    const loading = cache?.loading;
    const opciones = cache?.opciones || [];
    const st = Number(sensor.signalType);
    const tipoSeleccionado = tiposSensor.find((tp) => String(tp.id) === String(sensor.tipoParametroId));
    const forcePin = !!forzarCambioPin[sensor.id];
    if (![0, 1, 2].includes(st)) return <p role="alert">Esta interfaz no está soportada. Selecciona Analógica, Digital o 1-Wire antes de guardar.</p>;
    const recomendacionPines = recomendarPinesSensor(tipoSeleccionado?.nombre, st);

    if (loading) {
      return <p className="text-neutral-300 text-sm">Cargando pines disponibles…</p>;
    }

    if (st === SignalType.Uart) {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block mb-1">Pin Rx</label>
            <select
              value={sensor.pinRx || ""}
              onChange={(e) => handleChange(sensor.id, "pinRx", e.target.value)}
              title={recomendacionPines}
              className="w-full p-2 rounded bg-neutral-700 text-white"
              disabled={!puedeEditar}
            >
              {renderOptgroups(opciones, st, { disabledOption: (o) => o.pin === sensor.pinTx, allowOccupied: forcePin })}
            </select>
          </div>
          <div>
            <label className="block mb-1">Pin Tx</label>
            <select
              value={sensor.pinTx || ""}
              onChange={(e) => handleChange(sensor.id, "pinTx", e.target.value)}
              title={recomendacionPines}
              className="w-full p-2 rounded bg-neutral-700 text-white"
              disabled={!puedeEditar}
            >
              {renderOptgroups(opciones, st, { disabledOption: (o) => o.pin === sensor.pinRx, allowOccupied: forcePin })}
            </select>
          </div>
          <div className="md:col-span-2"><p className="mt-2 text-sm text-neutral-200">{recomendacionPines}</p>
        {renderNotasTipo(st)}</div>
        </div>
      );
    }

    // Analog / OneWire / Digital / I2c → pinPrincipal
    return (
      <div>
        <label className="block mb-1">GPIO de señal del ESP32 (AO, DO o DATA)</label>
        <select
          value={sensor.pinPrincipal || ""}
          onChange={(e) => handleChange(sensor.id, "pinPrincipal", e.target.value)}
          title={recomendacionPines}
          className="w-full p-2 rounded bg-neutral-700 text-white"
          disabled={!puedeEditar}
        >
          {renderOptgroups(opciones, st, { allowOccupied: forcePin })}
        </select>
        <label className="mt-2 flex items-center gap-2 text-sm text-amber-200">
          <input type="checkbox" checked={forcePin} onChange={(e) => setForzarCambioPin((prev) => ({ ...prev, [sensor.id]: e.target.checked }))} disabled={!puedeEditar} />
          Forzar cambio de pin: archivar el sensor o efector que ocupa el GPIO
        </label>
        <p className="mt-2 text-sm text-neutral-200">{recomendacionPines}</p>
        {renderNotasTipo(st)}
      </div>
    );
  };

  if (!puedeVer) {
    return (
      <div className="p-6 bg-neutral-950 text-neutral-300 rounded-lg max-w-4xl mx-auto">
        No tienes permisos para ver Sensores.
      </div>
    );
  }

  return (
    <div className="p-6 bg-neutral-900 text-white rounded-lg shadow-md">
      <GestionArchivo tipo="sensores" puedeEditar={puedeEditar} onChanged={cargarDatos} />
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-green-400 text-2xl font-semibold">Administrar Sensores</h3>
        {esSuper && (
          <span className="text-xs border border-emerald-500/60 px-2 py-1 rounded text-emerald-300">
            Superadministrador
          </span>
        )}
      </div>

      {sensores.length === 0 && !error && (
        <p className="text-center text-cyan-300">No hay sensores para mostrar.</p>
      )}

      <input value={busqueda} onChange={(e) => setBusqueda(e.target.value)}
        placeholder="Buscar por sensor, tipo, estanque, descripción o pin…"
        className="w-full mb-4 p-2.5 rounded bg-neutral-800 border border-cyan-700/40 text-white" />

      {sensoresPorEstanque.map(([estanqueNombre, sensoresGrupo]) => (
        <section key={estanqueNombre} className="mb-7">
          <h4 className="text-green-400 text-xl font-semibold mb-3 border-b border-emerald-700/40 pb-2">
            {estanqueNombre}
          </h4>
          {sensoresGrupo.map((sensor) => {
            const tipoNombre = tiposSensor.find((tipo) => String(tipo.id) === String(sensor.tipoParametroId))?.nombre || "Sin tipo";
            return (
        <details key={sensor.id} className="border border-cyan-700/40 rounded mb-3 bg-neutral-800 overflow-hidden">
          <summary className="cursor-pointer select-none px-4 py-3 hover:bg-neutral-700/70 transition-colors list-none">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="font-semibold text-cyan-200">{sensor.nombre || "Sin nombre"}</span>
              <span className="text-sm text-neutral-300">
                {tipoNombre} <span className="text-neutral-500 mx-2">•</span> {estanqueNombre}
              </span>
            </div>
          </summary>
          <div className="p-4 border-t border-cyan-700/30">
          <h4 className="text-cyan-300 text-lg mb-2">
            Sensor: <span className="font-semibold text-cyan-200">{sensor.nombre || "Sin nombre"}</span>
          </h4>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Nombre */}
            <div>
              <label className="block mb-1">Nombre</label>
              <input
                value={sensor.nombre}
                onChange={(e) => handleChange(sensor.id, "nombre", e.target.value)}
                className="w-full p-2 rounded bg-neutral-700 text-white"
                disabled={!puedeEditar}
              />
            </div>

            {/* Descripción */}
            <div>
              <label className="block mb-1">Descripción</label>
              <input
                value={sensor.descripcion || ""}
                onChange={(e) => handleChange(sensor.id, "descripcion", e.target.value)}
                className="w-full p-2 rounded bg-neutral-700 text-white"
                disabled={!puedeEditar}
              />
            </div>

            {/* Tipo de Sensor (catálogo) */}
            <div>
              <label className="block mb-1">Tipo de Sensor</label>
              <select
                value={sensor.tipoParametroId || ""}
                onChange={(e) => handleChange(sensor.id, "tipoParametroId", e.target.value)}
                className="w-full p-2 rounded bg-neutral-700 text-white"
                disabled={!puedeEditar}
              >
                <option value="">Seleccione</option>
                {(Array.isArray(tiposSensor) ? tiposSensor : []).map((tp) => (
                  <option key={tp.id} value={tp.id}>
                    {tp.nombre}
                  </option>
                ))}
              </select>
            </div>

            {/* Dispositivo */}
            <div>
              <label className="block mb-1">Dispositivo</label>
              <select
                value={sensor.dispositivoId || ""}
                onChange={(e) => {
                  const nuevoDispositivoId = e.target.value;
                  handleChange(sensor.id, "dispositivoId", nuevoDispositivoId);
                  handleChange(sensor.id, "pinPrincipal", "");
                  handleChange(sensor.id, "pinRx", "");
                  handleChange(sensor.id, "pinTx", "");
                  cargarPinesLibres({ ...sensor, dispositivoId: nuevoDispositivoId });
                }}
                className="w-full p-2 rounded bg-neutral-700 text-white"
                disabled title="Usa Trasladar en Archivo y reutilización para conservar el historial."
              >
                <option value="">Seleccione</option>
                {(Array.isArray(dispositivos) ? dispositivos : []).map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.nombre}
                  </option>
                ))}
              </select>
              <p className="text-xs text-neutral-400 mt-1">Para cambiar de dispositivo usa Trasladar en Archivo y reutilización.</p>
            </div>

            {/* Tipo de señal (V2) */}
            <div className="md:col-span-2">
              <label className="block mb-1">Tipo de señal</label>
              <select
                value={sensor.signalType}
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10);
                  handleChange(sensor.id, "signalType", v);
                  handleChange(sensor.id, "pinPrincipal", "");
                  handleChange(sensor.id, "pinRx", "");
                  handleChange(sensor.id, "pinTx", "");
                  cargarPinesLibres({ ...sensor, signalType: v });
                }}
                className="w-full p-2 rounded bg-neutral-700 text-white"
                title={recomendarTipoSenal(
                  tiposSensor.find((tp) => String(tp.id) === String(sensor.tipoParametroId))?.nombre
                )}
                disabled={!puedeEditar}
              >
                <option value={SignalType.Analog}>Analógica (ADC)</option>
                <option value={SignalType.OneWire}>1-Wire</option>
                <option value={SignalType.Digital}>Digital</option>
                <option value={SignalType.Uart} disabled>UART — no disponible</option>
                <option value={SignalType.I2c} disabled>I²C — no disponible</option>
              </select>
            </div>

            {/* Pines dinámicos */}
            <div className="md:col-span-2">{renderSelectPines(sensor)}</div>

            {/* Tiempo de lectura en minutos */}
            <div>
              <IntervaloLectura cantidad={sensor.tiempoSensor ?? ""} unidad={sensor.unidadTiempo}
                onCantidadChange={value => handleChange(sensor.id, "tiempoSensor", value)}
                onUnidadChange={value => handleChange(sensor.id, "unidadTiempo", value)} disabled={!puedeEditar} />
            </div>

            {/* Activo */}
            <div>
              <label className="block mb-1">Activo</label>
              <select
                value={sensor.estaActivo ? "true" : "false"}
                onChange={(e) => handleChange(sensor.id, "estaActivo", e.target.value === "true")}
                className="w-full p-2 rounded bg-neutral-700 text-white"
                disabled={!puedeEditar}
              >
                <option value="true">Activo</option>
                <option value="false">Inactivo</option>
              </select>
            </div>
          </div>

          {/* Permisos */}
          <div className="mt-4">
            <label className="block mb-2">Permisos</label>
            <div className="grid grid-cols-1 md:grid-cols-2 max-h-32 overflow-y-auto bg-neutral-900 p-2 rounded">
              {(Array.isArray(usuarios) ? usuarios : []).map((u) => {
                const checked = normalizarPermisos(sensor.permisos).includes(String(u.id));
                return (
                  <label key={u.id} className={`flex items-center text-sm ${!puedeEditar ? "opacity-60" : ""}`}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => togglePermiso(sensor.id, u.id)}
                      className="mr-2"
                      disabled={!puedeEditar}
                    />
                    {u.nombre} <span className="text-neutral-400">({u.email})</span>
                  </label>
                );
              })}
              {(Array.isArray(usuarios) ? usuarios : []).length === 0 && (
                <div className="text-neutral-400 text-sm">Sin usuarios disponibles.</div>
              )}
            </div>
          </div>

          {/* Guardar */}
          <div className="mt-4 flex items-center gap-3">
            <button
              onClick={() => actualizarSensor(sensor)}
              className={`px-4 py-2 rounded ${
                puedeEditar ? "bg-green-600 hover:bg-green-700 text-white" : "bg-neutral-700 text-neutral-300 cursor-not-allowed"
              }`}
              disabled={!puedeEditar}
            >
              Guardar cambios
            </button>

            {flash[sensor.id]?.ok && <span className="text-green-400 text-sm">{flash[sensor.id].ok}</span>}
            {flash[sensor.id]?.err && <span className="text-red-400 text-sm">{flash[sensor.id].err}</span>}
          </div>
          </div>
        </details>
            );
          })}
        </section>
      ))}

      {mensaje && <p className="text-green-400 mt-4">{mensaje}</p>}
      {error && <p className="text-red-400 mt-4">{error}</p>}
    </div>
  );
};

export default AdministrarSensores;
